import React, { createContext, useState, useEffect, useContext } from 'react';
import apiService from '../services/api';

// Create the context
const AuthContext = createContext();

// Custom hook to use the auth context
export const useAuth = () => useContext(AuthContext);

// Provider component
export const AuthProvider = ({ children }) => {
    const [currentUser, setCurrentUser] = useState(null);
    const [loading, setLoading] = useState(true);
    const [error, setError] = useState(null);

    // Check if user is logged in on initial load
    useEffect(() => {
        const checkUserLoggedIn = async () => {
            const token = localStorage.getItem('auth_token');

            if (token) {
                try {
                    const response = await apiService.getCurrentUser();
                    setCurrentUser(response.data);
                } catch (err) {
                    console.error('Failed to fetch user:', err);
                    localStorage.removeItem('auth_token');
                }
            }

            setLoading(false);
        };

        checkUserLoggedIn();
    }, []);

    // Login function
    const login = async (credentials) => {
        try {
            setError(null);
            const response = await apiService.login(credentials);
            localStorage.setItem('auth_token', response.data.token);
            setCurrentUser(response.data.user);
            return response.data;
        } catch (err) {
            setError(err.response?.data?.message || 'Login failed');
            throw err;
        }
    };

    // Register function
    const register = async (userData) => {
        try {
            setError(null);
            const response = await apiService.register(userData);
            localStorage.setItem('auth_token', response.data.token);
            setCurrentUser(response.data.user);
            return response.data;
        } catch (err) {
            setError(err.response?.data?.message || 'Registration failed');
            throw err;
        }
    };

    // Logout function
    const logout = () => {
        localStorage.removeItem('auth_token');
        setCurrentUser(null);
    };

    // Update user profile
    const updateProfile = async (userData) => {
        try {
            setError(null);
            const response = await apiService.updateUser(userData);
            setCurrentUser(response.data);
            return response.data;
        } catch (err) {
            setError(err.response?.data?.message || 'Failed to update profile');
            throw err;
        }
    };

    // Context value
    const value = {
        currentUser,
        loading,
        error,
        login,
        register,
        logout,
        updateProfile,
    };

    return (
        <AuthContext.Provider value={value}>
            {children}
        </AuthContext.Provider>
    );
};

export default AuthContext;